import { useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import {
  AppModal,
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  ListRow,
  ProtectedScreen,
  SectionHeader,
  SegmentedControl,
  Stack,
  TextField,
} from "~/components/ui";
import { usePlan } from "~/hooks/usePlan";
import { useList, useListActions } from "~/state/lists";
import { useTaskActions, useTasks } from "~/state/tasks";
import {
  CADENCE_OPTIONS,
  cadenceLabel,
  nextDueOn,
  WEEKDAY_LABELS,
} from "~/utils/cadence";
import type { TaskCadence } from "~/api/types";
import { spacing } from "~/styles/theme";
import type { List } from "~/state/sync";

export default function ListDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const list = useList(id);

  if (!list) {
    return (
      <ProtectedScreen title="List">
        <EmptyState title="This list is not on this device." />
      </ProtectedScreen>
    );
  }

  return <ListDetail list={list} />;
}

function ListDetail({ list }: { list: List }) {
  const router = useRouter();
  const plan = usePlan();
  const tasks = useTasks(list.id);
  const listActions = useListActions();
  const taskActions = useTaskActions();
  const [name, setName] = useState(list.name);
  const [addOpen, setAddOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [cadence, setCadence] = useState<TaskCadence>("once");
  const [weekday, setWeekday] = useState(new Date().getDay());
  const [monthDay, setMonthDay] = useState(String(new Date().getDate()));
  const [error, setError] = useState<string | null>(null);

  const open = tasks.filter(
    (task) => !(task.cadence === "once" && task.completed_at),
  );
  const done = tasks.filter(
    (task) => task.cadence === "once" && task.completed_at,
  );

  function addTask() {
    if (!title.trim()) return;
    try {
      const month = Number(monthDay) || new Date().getDate();
      taskActions.create({
        listId: list.id,
        title,
        cadence,
        weekday: cadence === "weekly" ? weekday : null,
        monthDay: cadence === "monthly" || cadence === "yearly" ? month : null,
        dueOn: nextDueOn({
          cadence,
          weekday,
          monthDay: month,
        }),
      });
      setTitle("");
      setCadence("once");
      setError(null);
      setAddOpen(false);
    } catch (addError) {
      setError(
        addError instanceof Error
          ? addError.message
          : "Unable to add this task.",
      );
    }
  }

  function saveName() {
    const trimmed = name.trim();
    if (!trimmed || trimmed === list.name) return;
    listActions.update(list.id, { name: trimmed });
  }

  return (
    <ProtectedScreen
      title={list.name}
      subtitle={`${open.length} open · ${done.length} finished once-offs`}
    >
      <Card gap="md">
        <SectionHeader title="Rename" />
        <TextField
          accessibilityLabel="List name"
          value={name}
          onChangeText={setName}
          onBlur={saveName}
        />
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Tasks"
          description="Tap a task to change its cadence or see history."
        />
        {open.length === 0 ? (
          <EmptyState
            title="No open tasks"
            description="Add a once-off, or something that comes back every week or month."
          />
        ) : (
          <Stack gap="sm">
            {open.map((task) => (
              <ListRow
                key={task.id}
                title={task.title}
                description={cadenceLabel(
                  task.cadence,
                  task.weekday,
                  task.month_day,
                )}
                onPress={() => router.push(`/tasks/${task.id}` as never)}
                meta={
                  <>
                    <Badge
                      tone={
                        task.due_on < new Date().toISOString().slice(0, 10)
                          ? "danger"
                          : "neutral"
                      }
                    >
                      Due {task.due_on}
                    </Badge>
                  </>
                }
              />
            ))}
          </Stack>
        )}

        {plan.tasks.allowed ? (
          <Button
            align="start"
            iconName="plus"
            onPress={() => setAddOpen(true)}
          >
            <ButtonText>Add a task</ButtonText>
          </Button>
        ) : (
          <AppText size="xs">{plan.tasks.message}</AppText>
        )}
      </Card>

      {!plan.tasks.allowed ? (
        <UpgradePrompt title="Need more than ten tasks?" />
      ) : null}

      {done.length > 0 ? (
        <Card gap="md">
          <SectionHeader title="Finished" />
          <Stack gap="sm">
            {done.map((task) => (
              <ListRow
                key={task.id}
                title={task.title}
                onPress={() => router.push(`/tasks/${task.id}` as never)}
                meta={<Badge tone="success">Done</Badge>}
              />
            ))}
          </Stack>
        </Card>
      ) : null}

      <Card gap="sm">
        <SectionHeader
          title="Delete list"
          description="Removes this list and its tasks from every device on the next sync."
        />
        <Button
          variant="danger"
          align="start"
          onPress={() => {
            listActions.remove(list.id);
            router.replace("/lists" as never);
          }}
        >
          <ButtonText>Delete list</ButtonText>
        </Button>
      </Card>

      <AppModal
        title="Add a task"
        visible={addOpen}
        onClose={() => setAddOpen(false)}
      >
        <Stack gap="md">
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Title
            </AppText>
            <TextField
              accessibilityLabel="Task title"
              value={title}
              placeholder="Water the herbs, pay rates, call Mum…"
              onChangeText={setTitle}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Repeats
            </AppText>
            <SegmentedControl
              value={cadence}
              options={CADENCE_OPTIONS}
              onChange={(value) => setCadence(value)}
            />
          </View>
          {cadence === "weekly" ? (
            <View style={stylesheet.field}>
              <AppText size="xs" weight="semibold">
                Weekday
              </AppText>
              <SegmentedControl
                value={String(weekday)}
                options={WEEKDAY_LABELS.map((label, index) => ({
                  label: label.slice(0, 3),
                  value: String(index),
                }))}
                onChange={(value) => setWeekday(Number(value))}
              />
            </View>
          ) : null}
          {cadence === "monthly" || cadence === "yearly" ? (
            <View style={stylesheet.field}>
              <AppText size="xs" weight="semibold">
                Day of month
              </AppText>
              <TextField
                accessibilityLabel="Day of month"
                keyboardType="number-pad"
                value={monthDay}
                onChangeText={setMonthDay}
              />
            </View>
          ) : null}
          {error ? (
            <AppText size="xs" tone="danger">
              {error}
            </AppText>
          ) : null}
          <Button iconName="plus" disabled={!title.trim()} onPress={addTask}>
            <ButtonText>Add task</ButtonText>
          </Button>
        </Stack>
      </AppModal>
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  field: { gap: spacing.xs },
});
